/**
 * Global notification listener.
 * - Subscribes to realtime `notifications` for the current user
 * - Shows a browser Notification when permission is already granted, in-app toast, plays a beep
 * - Tracks unread *message* notifications from the DB (single source of truth),
 *   so the badge survives reloads and is consistent across devices.
 *
 * Mounted once at the App root (inside AuthProvider).
 */
import { useEffect, useState, useCallback } from "react";
import { useTranslation } from "react-i18next";
import { supabase } from "@/integrations/supabase/client";
import { useAuth } from "@/contexts/auth-context";
import { toast } from "sonner";
import {
  normalizeNotificationItem,
  notificationActionUrl,
  shouldCountAsUnreadMessage,
  shouldPlayNotificationSound,
  shouldShowForegroundToast,
} from "@/lib/notificationInbox";

const playBeep = () => {
  try {
    const AudioContextCtor =
      window.AudioContext ||
      (window as Window & typeof globalThis & { webkitAudioContext?: typeof AudioContext }).webkitAudioContext;
    if (!AudioContextCtor) return;
    const ctx = new AudioContextCtor();
    const osc = ctx.createOscillator();
    const gain = ctx.createGain();
    osc.connect(gain);
    gain.connect(ctx.destination);
    osc.type = "sine";
    osc.frequency.value = 880;
    gain.gain.setValueAtTime(0.0001, ctx.currentTime);
    gain.gain.exponentialRampToValueAtTime(0.15, ctx.currentTime + 0.02);
    gain.gain.exponentialRampToValueAtTime(0.0001, ctx.currentTime + 0.35);
    osc.start();
    osc.stop(ctx.currentTime + 0.4);
  } catch {
    /* ignore audio errors */
  }
};

const showBrowserPush = (title: string, body: string, url?: string) => {
  if (!("Notification" in window)) return;
  if (Notification.permission !== "granted") return;
  // Don't double-notify when the tab is focused — toast covers that.
  if (document.visibilityState === "visible") return;
  try {
    const n = new Notification(title, {
      body,
      icon: "/pwa-192x192.png",
      badge: "/pwa-192x192.png",
      tag: url || title,
    });
    n.onclick = () => {
      window.focus();
      if (url) window.location.href = url;
      n.close();
    };
  } catch {
    /* ignore */
  }
};

export const useGlobalNotifications = (disabled = false) => {
  const { user } = useAuth();
  const { t } = useTranslation();
  const [unreadMessages, setUnreadMessages] = useState(0);

  // Recompute unread message count from the notifications table
  const recomputeUnread = useCallback(async () => {
    if (!user || disabled) {
      setUnreadMessages(0);
      return;
    }
    const { data } = await supabase
      .from("notifications")
      .select("id, metadata")
      .eq("user_id", user.id)
      .eq("type", "message")
      .eq("read", false);
    const unread = (data ?? [])
      .map(normalizeNotificationItem)
      .filter(shouldCountAsUnreadMessage)
      .length;
    setUnreadMessages(unread);
  }, [user, disabled]);

  // Subscribe to notifications: toast, push, recount
  useEffect(() => {
    if (!user || disabled) return;
    recomputeUnread();

    const channel = supabase
      .channel(`global-notif-${user.id}`)
      .on(
        "postgres_changes",
        { event: "INSERT", schema: "public", table: "notifications", filter: `user_id=eq.${user.id}` },
        (payload) => {
          const n = normalizeNotificationItem(payload.new);
          const url = notificationActionUrl(n);
          if (shouldShowForegroundToast(n)) {
            toast(n.title || t("runtime.notifications.newNotification"), {
              description: n.message,
              action: url
                ? { label: t("runtime.notifications.open"), onClick: () => (window.location.href = url) }
                : undefined,
            });
            showBrowserPush(n.title || t("runtime.notifications.newNotification"), n.message || "", url);
            if (shouldPlayNotificationSound(n)) playBeep();
          }
          if (shouldCountAsUnreadMessage(n)) {
            setUnreadMessages((c) => c + 1);
          }
        }
      )
      .on(
        "postgres_changes",
        { event: "UPDATE", schema: "public", table: "notifications", filter: `user_id=eq.${user.id}` },
        () => {
          // read flag may have changed — recount
          recomputeUnread();
        }
      )
      .subscribe();

    return () => {
      supabase.removeChannel(channel);
    };
  }, [user, disabled, recomputeUnread, t]);

  // Mark all message-type notifications as read in the DB
  const markMessagesSeen = useCallback(async () => {
    if (!user) return;
    setUnreadMessages(0);
    await supabase
      .from("notifications")
      .update({ read: true })
      .eq("user_id", user.id)
      .eq("type", "message")
      .eq("read", false);
  }, [user]);

  return { unreadMessages, markMessagesSeen };
};
